/**
 * What error classification needs to know about the request behind a failure.
 *
 * `sentToken` is the bearer token that was actually sent with the request — not
 * whatever token the app holds by the time the failure is classified — so that a
 * renewal racing the response cannot change how a 401 is treated.
 */
export type RequestMetadata = {
  operation: string;
  endpoint?: string;
  url?: string;
  sentToken: string | null;
  /** The stack of the code that made the request, for direct fetches. */
  callStack?: string;
};

// Keyed by the object that represents the failure (an Apollo network error, or
// a fetch Response), so metadata travels with it without mutating it.
const metadataByFailure = new WeakMap<WeakKey, RequestMetadata>();

export function recordRequestMetadata(
  failure: WeakKey,
  metadata: RequestMetadata
): void {
  metadataByFailure.set(failure, metadata);
}

export function getRequestMetadata(
  failure: unknown
): RequestMetadata | undefined {
  if (!failure || typeof failure !== "object") {
    return undefined;
  }

  return metadataByFailure.get(failure);
}
